import type { SandboxEngineTemplate } from '../gen/ai/h2o/engine/v1/sandbox_engine_template_pb';
import {
  type CreateSandboxEngineTemplateRequest,
  type CreateSandboxEngineTemplateResponse,
  DeleteSandboxEngineTemplateRequest,
  DeleteSandboxEngineTemplateResponse,
  type GetSandboxEngineTemplateRequest,
  type GetSandboxEngineTemplateResponse,
  type ListSandboxEngineTemplatesRequest,
  type ListSandboxEngineTemplatesResponse,
  SandboxEngineTemplateService_CreateSandboxEngineTemplate,
  SandboxEngineTemplateService_DeleteSandboxEngineTemplate,
  SandboxEngineTemplateService_GetSandboxEngineTemplate,
  SandboxEngineTemplateService_ListSandboxEngineTemplates,
  SandboxEngineTemplateService_UpdateSandboxEngineTemplate,
  type UpdateSandboxEngineTemplateRequest,
  type UpdateSandboxEngineTemplateResponse,
} from '../gen/ai/h2o/engine/v1/sandbox_engine_template_service_pb';
import { BigIntString } from '../gen/runtime';
import { GIBIBYTE, gibibytesToBigIntStringBytes, HOUR_SECONDS } from '../utils';
import {
  engineProfileFieldsConfig,
  engineProfilesColumns,
  EntityActionType,
  EntityFieldType,
  EntityType,
} from './constants';
import { Entity, EntityAction, EntityField } from './types';

const entityType = EntityType.SandboxEngineTemplate;

const required = true;

const DEFAULT_MILLI_CPU = 500;
const DEFAULT_MEMORY_GIB = 1;
const DEFAULT_STORAGE_GIB = 10;
const DEFAULT_GPU_COUNT = 0;
const DEFAULT_MAX_IDLE_DURATION_SECONDS = HOUR_SECONDS;

const toBigIntStringBytes = (value: number): BigIntString =>
  (gibibytesToBigIntStringBytes(value) ?? (value * GIBIBYTE).toString()) as BigIntString;

const DEFAULT_MEMORY_BYTES = toBigIntStringBytes(DEFAULT_MEMORY_GIB);
const DEFAULT_STORAGE_BYTES = toBigIntStringBytes(DEFAULT_STORAGE_GIB);

const columns = engineProfilesColumns.filter(({ key }) => key !== 'priority');

export const actions = {
  [EntityActionType.Create]: {
    name: 'CreateSandboxEngineTemplate',
    rpc: SandboxEngineTemplateService_CreateSandboxEngineTemplate,
    type: EntityActionType.Create,
    responseKey: 'sandboxEngineTemplate',
    requestNameKey: 'sandboxEngineTemplateId',
    requestPayloadKey: 'sandboxEngineTemplate',
    requestNameKeyPrefix: 'sandboxEngineTemplate/',
    entityType,
  } as EntityAction<SandboxEngineTemplate, CreateSandboxEngineTemplateRequest, CreateSandboxEngineTemplateResponse>,
  [EntityActionType.Get]: {
    name: 'GetSandboxEngineTemplate',
    rpc: SandboxEngineTemplateService_GetSandboxEngineTemplate,
    type: EntityActionType.Get,
    requestNameKey: 'name' as unknown,
    responseKey: 'sandboxEngineTemplate',
    entityType,
  } as EntityAction<SandboxEngineTemplate, GetSandboxEngineTemplateRequest, GetSandboxEngineTemplateResponse>,
  [EntityActionType.List]: {
    name: 'ListSandboxEngineTemplate',
    rpc: SandboxEngineTemplateService_ListSandboxEngineTemplates,
    type: EntityActionType.List,
    responseKey: 'sandboxEngineTemplates',
    entityType,
  } as EntityAction<SandboxEngineTemplate, ListSandboxEngineTemplatesRequest, ListSandboxEngineTemplatesResponse>,
  [EntityActionType.Update]: {
    name: 'UpdateSandboxEngineTemplate',
    rpc: SandboxEngineTemplateService_UpdateSandboxEngineTemplate,
    type: EntityActionType.Update,
    responseKey: 'sandboxEngineTemplate',
    requestNameKey: 'name' as unknown,
    requestPayloadKey: 'sandboxEngineTemplate',
    entityType,
  } as EntityAction<SandboxEngineTemplate, UpdateSandboxEngineTemplateRequest, UpdateSandboxEngineTemplateResponse>,
  [EntityActionType.Delete]: {
    name: 'DeleteSandboxEngineTemplate',
    rpc: SandboxEngineTemplateService_DeleteSandboxEngineTemplate,
    type: EntityActionType.Delete,
    entityType,
  } as EntityAction<SandboxEngineTemplate, DeleteSandboxEngineTemplateRequest, DeleteSandboxEngineTemplateResponse>,
};

const engineProfileFields = engineProfileFieldsConfig<SandboxEngineTemplate>('Sandbox');

const {
  name: nameField,
  displayName: displayNameField,
  enabled: enabledField,
  gpuResourceName: gpuResourceNameField,
  yamlPodTemplateSpec: yamlPodTemplateSpecField,
} = engineProfileFields;

const gpuResourceField: EntityField<SandboxEngineTemplate> = {
  ...gpuResourceNameField,
  name: 'gpuResource',
  label: 'GPU Resource',
};

export const fields: EntityField<SandboxEngineTemplate>[] = [
  nameField,
  displayNameField,
  enabledField,
  {
    name: 'milliCpuRequest',
    type: EntityFieldType.Number,
    label: 'Milli CPU Request',
    description: `
      MilliCPU units that will be reserved for the SandboxEngine.
      It is recommended to set CPU requests to avoid resource contention.
      1000 milliCPU = 1 CPU core.
    `,
    togglable: true,
    fieldUnsetText: 'Use limit value',
    inputDefaultValue: String(DEFAULT_MILLI_CPU),
  },
  {
    name: 'milliCpuLimit',
    type: EntityFieldType.Number,
    label: 'Milli CPU Limit',
    description: `Maximum MilliCPU units a SandboxEngine is allowed to use.`,
    togglable: true,
    fieldUnsetText: 'Unset',
    inputDefaultValue: String(DEFAULT_MILLI_CPU),
  },
  {
    name: 'memoryBytesRequest',
    type: EntityFieldType.Number,
    label: 'Memory Bytes Request',
    description: `Memory in bytes that will be reserved for the SandboxEngine.`,
    minimum: DEFAULT_MEMORY_BYTES,
    convertToGibibytes: true,
    togglable: true,
    fieldUnsetText: 'Use limit value',
  },
  {
    name: 'memoryBytesLimit',
    type: EntityFieldType.Number,
    label: 'Memory Bytes Limit',
    description: `Max memory in bytes a SandboxEngine is allowed to use.`,
    minimum: DEFAULT_MEMORY_BYTES,
    convertToGibibytes: true,
  },
  {
    name: 'gpu',
    type: EntityFieldType.Number,
    label: 'GPU',
    description: `The amount of GPU units requested by this SandboxEngine.`,
  },
  gpuResourceField,
  {
    name: 'storageBytes',
    type: EntityFieldType.Number,
    label: 'Storage Bytes',
    description: `Max ephemeral storage available to the SandboxEngine.`,
    togglable: true,
    fieldUnsetText: 'Use node disk',
    convertToGibibytes: true,
    inputDefaultValue: String(GIBIBYTE * DEFAULT_STORAGE_GIB),
  },
  {
    name: 'maxIdleDuration',
    label: 'Max Idle Duration',
    type: EntityFieldType.Duration,
    description: 'Maximum time a SandboxEngine can be idle before it is automatically terminated.',
    required,
  },
  {
    name: 'environmentalVariables',
    type: EntityFieldType.KeyValuePair,
    label: `Environmental Variables`,
    description: `Map of environmental variables that will be set in the SandboxEngine.`,
  },
  yamlPodTemplateSpecField,
];

export const sandboxEngineTemplateEntity: Entity<SandboxEngineTemplate> = {
  type: EntityType.SandboxEngineTemplate,
  name: 'sandboxEngineTemplates',
  createButtonLabel: 'Add Sandbox Engine Template',
  displayName: 'Sandbox Engine Template',
  displayNameKey: 'displayName',
  actions,
  columns,
  fields,
  requestNameKeyPrefix: 'workspaces/global/sandboxEngineTemplates/',
  emptyModel: {
    name: 'new-sandbox-engine-template-id',
    displayName: 'New Sandbox Engine Template',
    enabled: true,
    milliCpuRequest: DEFAULT_MILLI_CPU,
    milliCpuLimit: DEFAULT_MILLI_CPU,
    memoryBytesRequest: DEFAULT_MEMORY_BYTES,
    memoryBytesLimit: DEFAULT_MEMORY_BYTES,
    gpu: DEFAULT_GPU_COUNT,
    gpuResource: 'nvidia.com/gpu',
    storageBytes: DEFAULT_STORAGE_BYTES,
    maxIdleDuration: `${DEFAULT_MAX_IDLE_DURATION_SECONDS}s`,
    environmentalVariables: {},
    yamlPodTemplateSpec: '',
  },
  canBeReordered: false,
};
